import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BookOutlined,
  HomeOutlined,
  PlusOutlined,
  LogoutOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import WatercolorLoader from './WatercolorLoader';
import { useAuth } from '../AuthContext';
import { listProjects, listSeries } from '../illustration/api';
import { bookNavSubtitle, bookNavTitle, booksInSeries, looseProjects } from '../illustration/seriesNav';
import KipMark from './KipMark';
import './AppSidebar.css';

const STORAGE_KEY = 'artificiallly.sidebar.collapsed';

function EdgeArrow({ collapsed }) {
  return (
    <svg
      className="app-sidebar__edge-arrow"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden="true"
      focusable="false"
    >
      {/* Points left when expanded (collapse); right when collapsed (expand) */}
      <path
        d={collapsed ? 'M4.5 2.5 L8 6 L4.5 9.5' : 'M7.5 2.5 L4 6 L7.5 9.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AppSidebar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [projects, setProjects] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setListError(null);
    const [projectResult, seriesResult] = await Promise.allSettled([listProjects(), listSeries()]);
    if (projectResult.status === 'fulfilled') {
      setProjects(projectResult.value.projects || []);
    } else {
      setProjects([]);
      setListError(projectResult.reason?.message || 'Could not load projects');
    }
    setSeriesList(seriesResult.status === 'fulfilled' ? (seriesResult.value.series || []) : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('artificiallly:projects-changed', refresh);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('artificiallly:projects-changed', refresh);
    };
  }, [refresh]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <aside
      className="app-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      aria-label="Workspace"
    >
      <button
        type="button"
        className="app-sidebar__edge-toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        <EdgeArrow collapsed={collapsed} />
      </button>

      <div className="app-sidebar__brand">
        <div className="app-sidebar__wordmark">
          <KipMark size={collapsed ? 28 : 56} />
          {!collapsed && (
            <span className="app-sidebar__brand-name">Artificiallly</span>
          )}
        </div>
      </div>

      <nav className="app-sidebar__nav" aria-label="Primary">
        <NavLink to="/" end className="app-sidebar__link" title="Home">
          <span className="app-sidebar__link-icon" aria-hidden="true">
            <HomeOutlined />
          </span>
          <span className="app-sidebar__link-label">Home</span>
        </NavLink>
      </nav>

      <button
        type="button"
        className="app-sidebar__create"
        onClick={() => navigate('/new')}
        title="Create New"
      >
        <span className="app-sidebar__create-icon" aria-hidden="true">
          <PlusOutlined />
        </span>
        <span className="app-sidebar__create-label">Create New</span>
      </button>

      <div className="app-sidebar__projects">
        <div className="app-sidebar__section-label" id="project-list-label">
          Project List
        </div>
        <div
          className="app-sidebar__project-list"
          role="navigation"
          aria-labelledby="project-list-label"
        >
          {loading ? (
            <div className="app-sidebar__loading">
              <WatercolorLoader size="small" />
            </div>
          ) : (
            <>
              {seriesList.map((series) => {
                const books = booksInSeries(series, projects);
                const onSeries = location.pathname.startsWith(`/series/${series.id}`);
                return (
                  <div className="app-sidebar__series" key={series.id}>
                    <NavLink
                      to={`/series/${series.id}`}
                      className="app-sidebar__project"
                      title={series.title}
                      aria-current={onSeries ? 'page' : undefined}
                    >
                      <span className="app-sidebar__link-icon" aria-hidden="true">
                        <ReadOutlined />
                      </span>
                      <span className="app-sidebar__project-meta">
                        <span className="app-sidebar__project-title">{series.title}</span>
                      </span>
                    </NavLink>
                    {books.map((book) => (
                      <NavLink
                        key={book.id}
                        to={`/projects/${book.id}/chapters`}
                        className="app-sidebar__project app-sidebar__project--nested"
                        title={bookNavTitle(series, book)}
                        aria-current={
                          projectId === book.id || location.pathname.startsWith(`/projects/${book.id}`)
                            ? 'page'
                            : undefined
                        }
                      >
                        <span className="app-sidebar__link-icon" aria-hidden="true">
                          <BookOutlined />
                        </span>
                        <span className="app-sidebar__project-meta">
                          <span className="app-sidebar__project-title">{bookNavTitle(series, book)}</span>
                          {bookNavSubtitle(series, book) ? (
                            <span className="app-sidebar__project-sub">
                              {bookNavSubtitle(series, book)}
                            </span>
                          ) : null}
                        </span>
                      </NavLink>
                    ))}
                  </div>
                );
              })}
              {looseProjects(projects, seriesList).map((p) => (
                <NavLink
                  key={p.id}
                  to={`/projects/${p.id}/chapters`}
                  className="app-sidebar__project"
                  title={p.title}
                  aria-current={
                    projectId === p.id || location.pathname.startsWith(`/projects/${p.id}`)
                      ? 'page'
                      : undefined
                  }
                >
                  <span className="app-sidebar__link-icon" aria-hidden="true">
                    <BookOutlined />
                  </span>
                  <span className="app-sidebar__project-meta">
                    <span className="app-sidebar__project-title">{p.title}</span>
                    <span className="app-sidebar__project-sub">
                      {p.subtitle || `Bible v${p.bibleVersion}`}
                    </span>
                  </span>
                </NavLink>
              ))}
              {!projects.length && (
                <span
                  className="app-sidebar__project-meta"
                  style={{ paddingInline: 'var(--space-3)', color: 'var(--color-text-disabled)' }}
                >
                  {listError || 'No projects yet'}
                </span>
              )}
            </>
          )}
        </div>
        <NavLink
          to="/archived"
          className="app-sidebar__archived-link app-sidebar__archived-link--list"
          title="Archived projects"
        >
          Archived projects
        </NavLink>
        <div className="app-sidebar__archived">
          <NavLink
            to="/admin"
            className="app-sidebar__archived-link app-sidebar__admin-link"
            title="Admin"
          >
            Admin
          </NavLink>
          <NavLink
            to="/release-notes"
            className="app-sidebar__archived-link"
            title="Release notes"
          >
            Release notes
          </NavLink>
          <NavLink
            to="/expression-engine"
            className="app-sidebar__archived-link"
            title="Expression engine"
          >
            Expression engine
          </NavLink>
          <NavLink
            to="/animation-engine"
            className="app-sidebar__archived-link"
            title="Animation engine"
          >
            Animation engine
          </NavLink>
          <NavLink
            to="/plans"
            className="app-sidebar__archived-link"
            title="Plans"
          >
            Plans
          </NavLink>
        </div>
      </div>

      <div className="app-sidebar__footer">
        <div className="app-sidebar__user" title={user?.email || ''}>
          {user?.email}
        </div>
        <button
          type="button"
          className="app-sidebar__signout"
          onClick={signOut}
          title="Sign out"
        >
          <span className="app-sidebar__link-icon" aria-hidden="true">
            <LogoutOutlined />
          </span>
          <span className="app-sidebar__link-label">Sign out</span>
        </button>
      </div>
    </aside>
  );
}
